import React, { useMemo, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { KnowledgeNode, KnowledgeEdge } from './types';
import './KnowledgeGraph.css';

interface KnowledgeGraphProps {
  nodes: KnowledgeNode[];
  edges: KnowledgeEdge[];
  onNodeClick: (nodeId: number) => void;
  selectedNodeId?: number;
}

interface GraphNode {
  id: number;
  question: string;
  description?: string | null;
  status: string;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: number | GraphNode;
  target: number | GraphNode;
  relationship_type?: string;
}

const KnowledgeGraph: React.FC<KnowledgeGraphProps> = ({
  nodes,
  edges,
  onNodeClick,
  selectedNodeId
}) => {
  const graphRef = useRef<any>();
  const hasInitializedRef = useRef(false);

  // Transform data into graph format
  const graphData = useMemo(() => {
    // Create new node objects (clones) to avoid mutating dependencies
    const graphNodes: GraphNode[] = nodes.map(node => ({
      id: node.id,
      question: node.question,
      description: node.description,
      status: node.status
    }));

    const links: GraphLink[] = edges.map(edge => ({
      source: edge.from_node_id,
      target: edge.to_node_id,
      relationship_type: edge.relationship_type
    }));

    // Initialize nodes in a simple layout (circular if no edges, otherwise let force simulation handle it)
    if (graphNodes.length > 0) {
      const angleSpread = Math.PI * 2;
      const baseRadius = 200;
      
      graphNodes.forEach((node, index) => {
        const angle = (index / graphNodes.length) * angleSpread;
        node.x = Math.cos(angle) * baseRadius;
        node.y = Math.sin(angle) * baseRadius;
      });
    }

    return { nodes: graphNodes, links };
  }, [nodes, edges]);

  // Configure force simulation
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const graph = graphRef.current;
      
      // Configure link force
      const linkForce = graph.d3Force('link');
      if (linkForce) {
        linkForce.distance(150);
        linkForce.strength(0.5);
      }
      
      // Configure charge force
      const chargeForce = graph.d3Force('charge');
      if (chargeForce) {
        chargeForce.strength(-400);
      }
      
      // Add centering force
      const centerForce = graph.d3Force('center');
      if (centerForce) {
        centerForce.strength(0.1);
      }
      
      // Restart the simulation to apply changes
      graph.d3ReheatSimulation();
    }
  }, [graphData.nodes.length, graphData.links.length]);

  // Center view after initial layout
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
      const graph = graphRef.current;
      
      const timer = setTimeout(() => {
        graph.zoomToFit(400, 50);
        hasInitializedRef.current = true;
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [graphData.nodes.length]);

  // Handle node click
  const handleNodeClick = (node: GraphNode) => {
    onNodeClick(node.id);
  };

  // Get node color based on status
  const getNodeColor = (status: string): string => {
    switch (status) {
      case 'mastered':
        return '#28a745';
      case 'answered':
        return '#d4edda';
      case 'pending':
      default:
        return '#ffffff';
    }
  };

  // Get node border color based on selection
  const getNodeBorderColor = (node: GraphNode): string => {
    if (selectedNodeId === node.id) {
      return '#333333';
    }
    return '#cccccc';
  };

  // Get node border width based on selection
  const getNodeBorderWidth = (node: GraphNode): number => {
    if (selectedNodeId === node.id) {
      return 3;
    }
    return 1;
  };

  if (nodes.length === 0) {
    return (
      <div className="knowledge-graph-empty">
        <p>No nodes to display. Create nodes to see them in the graph view.</p>
      </div>
    );
  }

  return (
    <div className="knowledge-graph-container">
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        nodeId="id"
        linkDirectionalArrowLength={6}
        linkDirectionalArrowRelPos={1}
        linkDirectionalArrowColor="#666666"
        onNodeClick={handleNodeClick}
        // Force simulation configuration
        cooldownTicks={150}
        onEngineStop={() => {
          if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
            setTimeout(() => {
              if (graphRef.current) {
                graphRef.current.zoomToFit(400, 50);
                hasInitializedRef.current = true;
              }
            }, 100);
          }
        }}
        // Custom node rendering with labels below nodes
        nodeCanvasObject={(node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
          
          const label = node.question.length > 30 ? node.question.substring(0, 30) + '...' : node.question;
          const fontSize = Math.max(12 / globalScale, 8);
          const radius = 12;
          
          // Draw circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          ctx.fillStyle = getNodeColor(node.status);
          ctx.fill();
          ctx.strokeStyle = getNodeBorderColor(node);
          ctx.lineWidth = getNodeBorderWidth(node);
          ctx.stroke();
          
          // Draw label below node
          ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#333333';
          ctx.fillText(label, node.x, node.y + radius + 5);
        }}
        nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

          const pointerAreaRadius = 16;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, pointerAreaRadius, 0, 2 * Math.PI, false);
          ctx.fill();
        }}
        // Custom link rendering with arrows
        linkCanvasObject={(link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
          const source = link.source as GraphNode;
          const target = link.target as GraphNode;
          
          if (typeof source.x !== 'number' || typeof source.y !== 'number' || typeof target.x !== 'number' || typeof target.y !== 'number') return;
          
          // Calculate arrow position (near target node, accounting for node radius)
          const nodeRadius = 12;
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const unitX = dx / distance;
          const unitY = dy / distance;
          
          // Arrow head position (at target node edge)
          const arrowLength = 8;
          const arrowX = target.x - unitX * nodeRadius;
          const arrowY = target.y - unitY * nodeRadius;
          
          // Draw line (stop before arrow)
          ctx.beginPath();
          ctx.moveTo(source.x, source.y);
          ctx.lineTo(arrowX, arrowY);
          ctx.strokeStyle = '#e0e0e0';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw arrow head
          const angle = Math.atan2(dy, dx);
          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle - Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle + Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fillStyle = '#666666';
          ctx.fill();
        }}
        // Zoom and pan controls
        enableZoomInteraction={true}
        enablePanInteraction={true}
        minZoom={0.1}
        maxZoom={3}
      />
      
      {/* Legend */}
      <div className="knowledge-graph-legend">
        <div className="legend-item">
          <div className="legend-color pending"></div>
          <span>Pending</span>
        </div>
        <div className="legend-item">
          <div className="legend-color answered"></div>
          <span>Answered</span>
        </div>
        <div className="legend-item">
          <div className="legend-color mastered"></div>
          <span>Mastered</span>
        </div>
      </div>

      {/* Zoom controls */}
      <div className="knowledge-graph-zoom-controls">
        <button
          className="knowledge-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() * 1.2, 400);
            }
          }}
          title="Zoom In"
        >
          +
        </button>
        <button
          className="knowledge-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() / 1.2, 400);
            }
          }}
          title="Zoom Out"
        >
          −
        </button>
        <button
          className="knowledge-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoomToFit(400, 50);
            }
          }}
          title="Reset Zoom / Fit to Screen"
        >
          ⌂
        </button>
      </div>
    </div>
  );
};

export default KnowledgeGraph;
